import React from 'react';
import { Zap, ChevronUp } from 'lucide-react';
import { cn, getDefensiveSignal } from '../../lib/utils';

interface BenchAndBullpenProps {
  benchPlayers: any[];
  bullpenPlayers: any[];
  bullpenUnavailable?: { player: any; reason: string }[];
  bullpenOnRest?: { player: any; until: string }[];
  selectedPlayerId: string | null;
  handleBenchClick: () => void;
  handlePlayerClick: (playerId: string) => void;
  isBullpenOpen: boolean;
  setIsBullpenOpen: (isOpen: boolean) => void;
}

export const BenchAndBullpen = ({
  bullpenPlayers,
  bullpenUnavailable = [],
  bullpenOnRest = [],
  selectedPlayerId,
  handleBenchClick,
  handlePlayerClick,
  isBullpenOpen,
  setIsBullpenOpen
}: BenchAndBullpenProps) => {
  return (
    <div
      className={cn("bg-surface rounded-3xl p-4 border transition-all", selectedPlayerId ? "border-emerald-500/50 cursor-pointer hover:bg-hairline" : "border-hairline", !isBullpenOpen && "cursor-pointer hover:bg-hairline")}
      onClick={(e) => {
        if (selectedPlayerId) {
          handleBenchClick();
        } else if (!isBullpenOpen) {
          setIsBullpenOpen(true);
        }
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[10px] font-bold text-ink-2 uppercase tracking-widest flex items-center space-x-2">
          <Zap className="w-3 h-3" />
          <span>Bullpen</span>
        </h3>
        {isBullpenOpen && (
          <button onClick={(e) => { e.stopPropagation(); setIsBullpenOpen(false); }} className="p-1 hover:bg-hairline rounded">
            <ChevronUp className="w-4 h-4 text-ink-2" />
          </button>
        )}
      </div>
      {isBullpenOpen ? (
        <div className="space-y-2">
          {bullpenPlayers.map((player: any) => (
            <button
              key={player.id}
              onClick={(e) => { e.stopPropagation(); handlePlayerClick(player.id); }}
              className={cn("w-full bg-raised p-2 rounded-xl border flex items-center justify-between transition-colors text-left", selectedPlayerId === player.id ? "border-emerald-500 bg-emerald-500/10" : "border-hairline/50 hover:border-emerald-500/50")}
            >
              <div className="flex items-center space-x-2">
                <div className={cn("w-2 h-2 rounded-full", getDefensiveSignal(player, 'P'))} />
                <div className="text-[10px] font-black uppercase">
                  {(() => {
                    const parts = (player.name || '').trim().split(/\s+/).filter(Boolean);
                    return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0] || '—';
                  })()}
                </div>
              </div>
              <div className="text-[8px] font-bold text-ink-2">{player.position}</div>
            </button>
          ))}
          {bullpenPlayers.length === 0 && bullpenUnavailable.length === 0 && bullpenOnRest.length === 0 && (
            <div className="p-2 text-center border border-dashed border-hairline rounded-xl">
              <p className="text-[8px] font-bold text-ink-2 uppercase tracking-widest">Empty</p>
            </div>
          )}
          {bullpenOnRest.length > 0 && (
            <div className="mt-2">
              <p className="text-[8px] font-black uppercase tracking-widest text-amber-500/60 px-1 mb-1">Mandatory Rest</p>
              {bullpenOnRest.map(({ player, until }) => (
                <button
                  key={player.id}
                  onClick={(e) => { e.stopPropagation(); handlePlayerClick(player.id); }}
                  className={cn("w-full bg-amber-500/5 p-2 rounded-xl border flex items-center justify-between transition-colors text-left mb-1", selectedPlayerId === player.id ? "border-amber-400 bg-amber-500/15" : "border-amber-500/20 hover:border-amber-400/50")}
                >
                  <div className="flex items-center space-x-2">
                    <div className={cn("w-2 h-2 rounded-full", getDefensiveSignal(player, 'P'))} />
                    <div className="text-[10px] font-black uppercase">
                      {(() => {
                        const parts = (player.name || '').trim().split(/\s+/).filter(Boolean);
                        return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0] || '—';
                      })()}
                    </div>
                  </div>
                  <div className="text-[8px] font-bold text-gold-ink uppercase tracking-tight">Until {until}</div>
                </button>
              ))}
            </div>
          )}
          {bullpenUnavailable.length > 0 && (
            <div className="mt-2">
              <p className="text-[8px] font-black uppercase tracking-widest text-ink-2 px-1 mb-1">Unavailable</p>
              {bullpenUnavailable.map(({ player, reason }) => (
                <div key={player.id} className="flex items-center justify-between px-2 py-1.5 opacity-50">
                  <span className="text-[9px] font-bold text-ink-2 uppercase truncate max-w-[90px]">{player.name}</span>
                  <span className="text-[8px] font-bold text-ink-2 uppercase tracking-tight text-right">{reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-[10px] font-bold text-ink-2 uppercase tracking-widest text-center py-4">
          Tap to open
        </div>
      )}
    </div>
  );
};
